import React from 'react';

import { avatarDefault } from '@/assets/images';
import { Divider, Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardianPlayer.inviteGuardian;
const AVATAR_SIZE = 60;

type Props = {
  name: string;
  /** Blue detail line, e.g. "Age 8 · Level 3 · Pinehurst". */
  detailLine: string;
  /** Muted membership line, e.g. "Since Sep 2023". */
  sinceLine: string;
  /** Bullet list of what the invite grants. */
  benefits: string[];
  avatarUrl?: string;
};

/** Read-only card summarising the player and what the invitation will include. */
export default function InviteInfoCard({
  name,
  detailLine,
  sinceLine,
  benefits,
  avatarUrl,
}: Props) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Stack style={styles.card} gap={scale(10)}>
      <Stack direction="row" align="center" gap={scale(12)}>
        <Image
          source={avatarUrl ? { uri: avatarUrl } : avatarDefault}
          width={scale(AVATAR_SIZE)}
          height={scale(AVATAR_SIZE)}
          circular
          resizeMode="cover"
          defaultSource={avatarDefault}
        />
        <Stack flex={1} gap={scale(2)}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.name}>
            {name}
          </Text>
          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.detail}>
            {detailLine}
          </Text>
          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.since}>
            {sinceLine}
          </Text>
        </Stack>
      </Stack>

      <Divider color={guardianColors['3']} />

      <Stack direction="row" align="center" gap={scale(4)}>
        <SvgIcon name="IcListCheck" size={scale(16)} color={guardianColors['6']} />
        <Text variant="Font Guardian App/Subtitle/Subtitle 11 Semibold" style={styles.infoTitle}>
          {strings.infoTitle}
        </Text>
      </Stack>

      <Stack gap={scale(8)}>
        {benefits.map(benefit => (
          <Stack key={benefit} direction="row" align="flex-start" gap={scale(8)}>
            <SvgIcon name="IcCheckMd" size={scale(16)} color={guardianColors['10']} />
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.benefit}>
              {benefit}
            </Text>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    width: '100%',
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
  },
  name: {
    color: guardianColors['10'],
  },
  detail: {
    color: guardianColors['Main/Main'],
  },
  since: {
    color: guardianColors['6'],
  },
  infoTitle: {
    color: guardianColors['6'],
  },
  benefit: {
    flex: 1,
    color: guardianColors['10'],
  },
}));
